// src/services/appraisalService.js
// FuMind_GM HR - Performance Appraisal Service
// Clean full replacement v11 Fix4. Sends recommendation fields and question snapshots in ScoreDetailsJson. Do not paste patch snippets into this file.

const APPRAISAL_LOAD_FLOW_URL = "/tracka-flow/v1/APPRAISAL_LOAD";

const APPRAISAL_ACTION_FLOW_URL = "/tracka-flow/v1/APPRAISAL_ACTION";

const APPRAISAL_API_KEY = "";

export const REVIEW_CYCLE_OPTIONS = ["Q1", "H1", "Annual"];
export const DEFAULT_REVIEW_CYCLE = "Annual";

function safeJsonParse(value, fallback = null) {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

function parseFlowArray(value) {
  if (Array.isArray(value)) return value;
  const parsed = safeJsonParse(value, []);
  return Array.isArray(parsed) ? parsed : [];
}

function readableValue(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => readableValue(item)).filter(Boolean).join(" | ");
  if (typeof value === "object") return value.message || value.error || value.detail || value.details || value.body?.message || value.error?.message || JSON.stringify(value, null, 2);
  return String(value);
}

function text(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return text(value.Value ?? value.value ?? value.Title ?? value.DisplayName ?? value.Email ?? "", fallback);
  return String(value).trim() || fallback;
}

function norm(value) {
  return text(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function safeReviewCycle(reviewCycle) {
  return REVIEW_CYCLE_OPTIONS.includes(reviewCycle) ? reviewCycle : DEFAULT_REVIEW_CYCLE;
}

function templateIdOf(template = {}) {
  return template.TemplateId || template.templateId || "";
}

function dedupeTemplates(rows = []) {
  const seen = new Set();
  const result = [];
  for (const row of rows || []) {
    const id = templateIdOf(row);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(row);
  }
  return result;
}

export function normalizeUser(user = {}) {
  return {
    employeeId: user.employeeId || user.employeeCode || user.EmployeeID || user.EmployeeCode || "",
    employeeCode: user.employeeCode || user.employeeId || user.EmployeeCode || user.EmployeeID || "",
    workEmail: user.workEmail || user.email || user.WorkEmail || user.Email || "",
    email: user.email || user.workEmail || user.Email || user.WorkEmail || "",
    role: user.role || user.Role || "Employee",
    displayName: user.displayName || user.name || user.employeeName || user.EmployeeName || "",
  };
}

async function postToFlow(url, body) {
  if (!url || url.includes("PASTE_")) throw new Error("Performance Appraisal flow URL is not configured.");
  let response;
  let data;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({  ...body }),
    });
    const raw = await response.text();
    data = safeJsonParse(raw, { raw });
  } catch (err) {
    throw new Error(`Unable to call appraisal flow: ${readableValue(err?.message || err)}`);
  }
  if (!response.ok) throw new Error(`Appraisal flow failed (${response.status}): ${readableValue(data?.message || data?.error || data?.raw || data)}`);
  if (data?.success === false) throw new Error(readableValue(data?.message || data?.error || data, "Appraisal flow returned failure."));
  return data;
}

export function getCurrentReviewCycle() { return DEFAULT_REVIEW_CYCLE; }

export async function loadAppraisalData({ currentUser, reviewCycle }) {
  const data = await postToFlow(APPRAISAL_LOAD_FLOW_URL, {
    action: "loadAppraisalData",
    reviewCycle: safeReviewCycle(reviewCycle),
    currentUser: normalizeUser(currentUser),
  });
  return {
    selfReviews: parseFlowArray(data?.selfReviews),
    teamEmployees: parseFlowArray(data?.teamEmployees),
    teamReviews: parseFlowArray(data?.teamReviews),
    allEmployees: parseFlowArray(data?.allEmployees),
    allReviews: parseFlowArray(data?.allReviews),
    raw: data,
  };
}

export async function loadTemplates({ currentUser, reviewCycle } = {}) {
  const data = await postToFlow(APPRAISAL_LOAD_FLOW_URL, {
    action: "loadTemplates",
    reviewCycle: safeReviewCycle(reviewCycle),
    currentUser: normalizeUser(currentUser),
  });
  return parseFlowArray(data?.templates);
}

export async function loadTemplateQuestions({ currentUser, templateId }) {
  const data = await postToFlow(APPRAISAL_LOAD_FLOW_URL, {
    action: "loadTemplateQuestions",
    templateId,
    currentUser: normalizeUser(currentUser),
  });
  return parseFlowArray(data?.questions);
}

export async function loadTemplateAssignments({ currentUser, targetEmployeeCode, reviewCycle = DEFAULT_REVIEW_CYCLE } = {}) {
  if (!targetEmployeeCode) return [];
  const data = await postToFlow(APPRAISAL_LOAD_FLOW_URL, {
    action: "loadTemplateAssignments",
    targetEmployeeCode,
    reviewCycle: safeReviewCycle(reviewCycle),
    currentUser: normalizeUser(currentUser),
  });
  return parseFlowArray(data?.assignments || data?.templateAssignments);
}

export function resolveBestTemplateFromCandidates({ targetEmployee = {}, candidateTemplates = [], reviewType = "SelfReview" }) {
  const emp = {
    countryCode: norm(targetEmployee.countryCode || targetEmployee.CountryCode),
    industryType: norm(targetEmployee.industryType || targetEmployee.IndustryType),
    departmentCode: norm(targetEmployee.departmentCode || targetEmployee.DepartmentCode),
    employmentType: norm(targetEmployee.employmentType || targetEmployee.EmploymentType),
  };
  const scored = (candidateTemplates || []).map((template) => {
    let score = 0;
    const templateReviewType = text(template.ReviewType || template.reviewType);
    if (templateReviewType && templateReviewType !== reviewType) return { template, score: -1 };
    const checks = [
      [template.CountryCode || template.countryCode, emp.countryCode, 20],
      [template.IndustryType || template.industryType, emp.industryType, 20],
      [template.DepartmentCode || template.departmentCode, emp.departmentCode, 15],
      [template.EmploymentType || template.employmentType, emp.employmentType, 15],
    ];
    for (const [templateValue, employeeValue, exactWeight] of checks) {
      const t = norm(templateValue);
      if (!t || t === "all" || t === "global") score += 1;
      else if (employeeValue && t === employeeValue) score += exactWeight;
      else return { template, score: -1 };
    }
    return { template, score };
  });
  return scored.filter((row) => row.score >= 0).sort((a, b) => b.score - a.score)[0]?.template || null;
}

export async function resolveEmployeeTemplates({ currentUser, targetEmployeeCode, reviewCycle, reviewType }) {
  const data = await postToFlow(APPRAISAL_LOAD_FLOW_URL, {
    action: "resolveEmployeeTemplate",
    targetEmployeeCode,
    reviewCycle: safeReviewCycle(reviewCycle),
    reviewType,
    currentUser: normalizeUser(currentUser),
  });
  const assignedTemplates = dedupeTemplates(parseFlowArray(data?.assignedTemplates));
  const resolvedTemplates = dedupeTemplates(parseFlowArray(data?.resolvedTemplates));
  const candidateTemplates = dedupeTemplates(parseFlowArray(data?.candidateTemplates));
  if (resolvedTemplates.length) return { ...data, resolvedTemplates, assignedTemplates, candidateTemplates };
  if (assignedTemplates.length) return { ...data, resolvedTemplates: assignedTemplates, assignedTemplates, candidateTemplates };
  const single = data?.resolvedTemplate || data?.template || null;
  if (single && templateIdOf(single)) return { ...data, resolvedTemplates: [single], assignedTemplates: [], candidateTemplates };
  const best = resolveBestTemplateFromCandidates({ targetEmployee: data?.targetEmployee || {}, candidateTemplates, reviewType });
  return { ...data, resolvedTemplates: best ? [best] : [], assignedTemplates: [], candidateTemplates };
}

export async function resolveEmployeeTemplate(args) {
  const data = await resolveEmployeeTemplates(args);
  return { ...data, resolvedTemplate: data.resolvedTemplates?.[0] || null };
}

export function calculateAppraisalScores({ questions = [], answers = [], template = {} }) {
  const answerMap = new Map((answers || []).map((answer) => [answer.questionId, answer]));
  const rows = (questions || [])
    .map((question) => {
      const id = question.QuestionId || question.questionId;
      const answer = answerMap.get(id) || {};
      return {
        category: text(question.ScoreCategory || question.scoreCategory || question.SectionCode || question.sectionCode, "Technical"),
        score: Number(answer.score || 0),
        weight: Number(question.Weight || question.weight || 1),
        isScored: (question.IsScored ?? question.isScored ?? true) !== false,
      };
    })
    .filter((row) => row.isScored);
  const average = (category) => {
    const selected = rows.filter((row) => text(row.category).toLowerCase() === category.toLowerCase());
    const totalWeight = selected.reduce((sum, row) => sum + row.weight, 0);
    if (!totalWeight) return 0;
    return Number((selected.reduce((sum, row) => sum + row.score * row.weight, 0) / totalWeight).toFixed(2));
  };
  const technicalScore = average("Technical");
  const behaviourScore = average("Behaviour");
  const potentialScore = average("Potential");
  const technicalWeight = Number(template.TechnicalWeight ?? template.technicalWeight ?? 60);
  const behaviourWeight = Number(template.BehaviourWeight ?? template.behaviourWeight ?? 40);
  const potentialWeight = Number(template.PotentialWeight ?? template.potentialWeight ?? 0);
  const totalWeight = Math.max(technicalWeight + behaviourWeight + potentialWeight, 1);
  const overallRating = Number(((technicalScore * technicalWeight + behaviourScore * behaviourWeight + potentialScore * potentialWeight) / totalWeight).toFixed(2));
  return { technicalScore, behaviourScore, potentialScore, overallRating };
}

export async function saveTemplate({ currentUser, payload }) {
  return postToFlow(APPRAISAL_ACTION_FLOW_URL, { action: "saveTemplate", currentUser: normalizeUser(currentUser), payload });
}

export async function saveTemplateQuestion({ currentUser, payload }) {
  return postToFlow(APPRAISAL_ACTION_FLOW_URL, { action: "saveTemplateQuestion", currentUser: normalizeUser(currentUser), payload });
}

export async function saveTemplateAssignment({ currentUser, payload }) {
  return postToFlow(APPRAISAL_ACTION_FLOW_URL, { action: "saveTemplateAssignment", currentUser: normalizeUser(currentUser), payload });
}

export async function saveAppraisalReview({ currentUser, reviewType, reviewCycle, employeeId, employeeName, template, questions = [], answers = [], comments = "", reviewStatus = "Submitted", managerRecommendation = "", managerRecommendationReason = "", hrRecommendation = "", hrRecommendationReason = "", finalDecision = "", compensationRecommendation = "", developmentPlan = "" }) {
  const user = normalizeUser(currentUser);
  const cycle = safeReviewCycle(reviewCycle);
  const calculated = calculateAppraisalScores({ questions, answers, template });
  const questionDetails = (questions || []).map((question) => ({ questionId: question.QuestionId || question.questionId || "", sectionCode: question.SectionCode || question.sectionCode || "", sectionName: question.SectionName || question.sectionName || "", questionText: question.QuestionText || question.questionText || "", helpText: question.HelpText || question.helpText || "", reviewerRole: question.ReviewerRole || question.reviewerRole || "", scoreCategory: question.ScoreCategory || question.scoreCategory || "", questionType: question.QuestionType || question.questionType || "", weight: question.Weight || question.weight || 1, maxScore: question.MaxScore || question.maxScore || 5 }));
  const reviewExtras = { managerRecommendation, managerRecommendationReason, hrRecommendation, hrRecommendationReason, finalDecision, compensationRecommendation, developmentPlan };
  const scoreDetails = { templateId: template?.TemplateId || template?.templateId || "", templateName: template?.TemplateName || template?.templateName || "", reviewType, reviewCycle: cycle, questions: questionDetails, answers, calculated, reviewExtras };
  return postToFlow(APPRAISAL_ACTION_FLOW_URL, { action: "saveReview", reviewType, reviewCycle: cycle, currentUser: user, payload: { EmployeeID: employeeId, EmployeeName: employeeName || employeeId, ReviewerID: user.employeeCode || user.employeeId, ReviewerName: user.displayName || user.employeeCode || user.employeeId, ReviewerRole: user.role, ReviewCycle: cycle, ReviewType: reviewType, TemplateId: template?.TemplateId || template?.templateId || "", TemplateName: template?.TemplateName || template?.templateName || "", TechnicalScore: calculated.technicalScore, BehaviourScore: calculated.behaviourScore, OverallRating: calculated.overallRating, Comments: comments || "", ManagerRecommendation: managerRecommendation || "", ManagerRecommendationReason: managerRecommendationReason || "", HRRecommendation: hrRecommendation || "", HRRecommendationReason: hrRecommendationReason || "", FinalDecision: finalDecision || "", CompensationRecommendation: compensationRecommendation || "", DevelopmentPlan: developmentPlan || "", ScoreDetailsJson: scoreDetails, ReviewStatus: reviewStatus } });
}
